import React, { useState, useEffect } from "react";
import { useModalForm } from "../../hooks/useModalForm";
import FieldError from "../fields/FieldError";
import Label from "../fields/Label";
import TextInput from "../fields/TextInput";
import { fieldClass, selectClass } from "../fields/fieldStyles";
import { MdClose, MdAdd } from "react-icons/md";
import ModalShell from "../modals/ModalShell";
import Button from "../common/Button";
import {
  TrainingProgram,
  TrainingProgramType,
  DifficultyLevel,
  WorkoutDuration,
} from "../../types/program";
import {
  getTrainingProgramTypeOptions,
  getDifficultyLevelOptions,
  getWorkoutDurationOptions,
  normalizeDifficultyLevels,
} from "../../utils/programUtils";
import { logger } from "../../utils/logger";

interface ProgramModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (programData: any) => Promise<void>;
  program?: TrainingProgram | null;
  mode: "create" | "edit";
  loading?: boolean;
  error?: string | null;
}

const ProgramModal: React.FC<ProgramModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  program,
  mode,
  loading = false,
  error: responseError,
}) => {
  const [equipmentInput, setEquipmentInput] = useState("");

  const {
    values: formData,
    setValue: handleInputChange,
    setValues: setFormData,
    errors,
    setErrors,
    isSubmitting,
    handleSubmit,
  } = useModalForm({
    isOpen,
    initialValues:
      mode === "edit" && program
        ? {
            name: program.name || "",
            program_type: program.program_type || TrainingProgramType.CARDIO,
            difficulty_level: (() => {
              const normalized = normalizeDifficultyLevels(
                (program as any).difficulty_levels || program.difficulty_level
              );
              return normalized.length > 0
                ? normalized
                : [DifficultyLevel.BEGINNER];
            })(),
            duration_weeks: program.duration_weeks || 4,
            training_days_per_week: program.training_days_per_week || 3,
            workout_duration:
              program.workout_duration || WorkoutDuration.FIFTEEN_TO_THIRTY,
            description: program.description || "",
            equipment: (program.equipment || []) as string[],
            is_visible:
              program.is_visible !== undefined ? program.is_visible : true,
          }
        : {
            name: "",
            program_type: TrainingProgramType.CARDIO,
            difficulty_level: [DifficultyLevel.BEGINNER] as DifficultyLevel[],
            duration_weeks: 4,
            training_days_per_week: 3,
            workout_duration: WorkoutDuration.FIFTEEN_TO_THIRTY,
            description: "",
            equipment: [] as string[],
            is_visible: true,
          },
    validate: (v) => {
      const next: Record<string, string> = {};
      const selectedDifficultyLevels = normalizeDifficultyLevels(
        v.difficulty_level
      );

      if (!v.name || v.name.trim() === "") {
        next.name = "Program name is required";
      } else if (v.name.length > 255) {
        next.name = "Program name must not exceed 255 characters";
      }

      if (!v.program_type || v.program_type.trim() === "") {
        next.program_type = "Program type is required";
      }

      if (selectedDifficultyLevels.length === 0) {
        next.difficulty_level = "Select at least one difficulty level";
      }

      if (mode === "create") {
        if (!v.duration_weeks || v.duration_weeks < 1) {
          next.duration_weeks = "Duration must be at least 1 week";
        }

        if (
          !v.training_days_per_week ||
          v.training_days_per_week < 1 ||
          v.training_days_per_week > 7
        ) {
          next.training_days_per_week =
            "Training days per week must be between 1 and 7";
        }

        if (!v.workout_duration || v.workout_duration.trim() === "") {
          next.workout_duration = "Workout duration is required";
        }
      }

      if (v.equipment && Array.isArray(v.equipment)) {
        if (v.equipment.some((item: string) => item.length > 100)) {
          next.equipment = "Equipment items must not exceed 100 characters";
        }
      }

      return next;
    },
    onSubmit: async (v) => {
      try {
        const cleanedData: any = {
          name: v.name.trim(),
          program_type: v.program_type.trim(),
          difficulty_level: normalizeDifficultyLevels(v.difficulty_level),
          duration_weeks: parseInt(v.duration_weeks.toString()),
          training_days_per_week: parseInt(v.training_days_per_week.toString()),
          workout_duration: v.workout_duration.trim(),
          description: v.description?.trim() || null,
          is_visible: v.is_visible,
        };

        // Only include equipment in edit mode
        if (mode === "edit") {
          cleanedData.equipment =
            v.equipment && v.equipment.length > 0 ? v.equipment : null;
        }

        await onSubmit(cleanedData);
      } catch (error) {
        logger.error("Error submitting form:", error);
      }
    },
  });

  // The equipment chip input is not a form value, so it resets alongside it.
  useEffect(() => {
    if (isOpen) setEquipmentInput("");
  }, [isOpen]);

  const handleAddEquipment = () => {
    const trimmedInput = equipmentInput.trim();
    if (!trimmedInput) return;

    if (trimmedInput.length > 100) {
      setErrors((prev) => ({
        ...prev,
        equipment: "Equipment item must not exceed 100 characters",
      }));
      return;
    }

    if (formData.equipment.includes(trimmedInput)) {
      setErrors((prev) => ({
        ...prev,
        equipment: "This equipment is already added",
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      equipment: [...prev.equipment, trimmedInput],
    }));
    setEquipmentInput("");
    setErrors((prev) => ({ ...prev, equipment: undefined }));
  };

  const handleDifficultyToggle = (level: DifficultyLevel) => {
    setFormData((prev) => {
      const currentLevels = Array.isArray(prev.difficulty_level)
        ? prev.difficulty_level
        : [];
      const exists = currentLevels.includes(level);

      return {
        ...prev,
        difficulty_level: exists
          ? currentLevels.filter((item: DifficultyLevel) => item !== level)
          : [...currentLevels, level],
      };
    });
    setErrors((prev) => ({ ...prev, difficulty_level: undefined }));
  };

  const handleRemoveEquipment = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      equipment: prev.equipment.filter((_: string, i: number) => i !== index),
    }));
  };

  const handleEquipmentKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddEquipment();
    }
  };

  if (!isOpen) return null;

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      title={mode === "create" ? "Create New Program" : "Edit Program"}
      size="2xl"
      closeDisabled={isSubmitting}
      message={responseError ? { text: responseError, type: "error" } : null}
      onSubmit={handleSubmit}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            loading={isSubmitting}
            loadingLabel="Saving..."
            disabled={loading}
          >
            {mode === "create" ? "Create Program" : "Update Program"}
          </Button>
        </>
      }
    >
      <div className="p-6">
        <div className="space-y-4">
          {/* Program Name */}
          <div>
            <Label>Program Name *</Label>
            <TextInput
              type="text"
              value={formData.name || ""}
              onChange={(e) => handleInputChange("name", e.target.value)}
              maxLength={255}
              invalid={!!errors.name}
              placeholder="Enter program name"
            />
            <FieldError>{errors.name}</FieldError>
          </div>

          <div className="grid grid-cols-1 gap-4">
            {/* Program Type */}
            <div>
              <Label>Program Type *</Label>
              <select
                value={formData.program_type || ""}
                onChange={(e) =>
                  handleInputChange("program_type", e.target.value)
                }
                className={selectClass(!!errors.program_type)}
              >
                {getTrainingProgramTypeOptions().map(({ value, label }) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
              <FieldError>{errors.program_type}</FieldError>
            </div>

            {/* Difficulty Level */}
            <div>
              <Label>Difficulty Level *</Label>
              <div
                className={`space-y-2 rounded-md border p-3 ${
                  errors.difficulty_level
                    ? "border-red-500"
                    : "border-gray-200 dark:border-gray-700"
                }`}
              >
                {getDifficultyLevelOptions().map(({ value, label }) => {
                  const selectedLevels = Array.isArray(
                    formData.difficulty_level
                  )
                    ? formData.difficulty_level
                    : [];
                  const checked = selectedLevels.includes(value);
                  return (
                    <label
                      key={value}
                      className="flex cursor-pointer items-center rounded-md p-2 hover:bg-gray-50 dark:hover:bg-navy-700"
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => handleDifficultyToggle(value)}
                        className="h-4 w-4 rounded border-gray-300 text-biafit-primary focus:ring-biafit-primary"
                      />
                      <span className="ml-3 text-sm text-gray-700 dark:text-gray-300">
                        {label}
                      </span>
                    </label>
                  );
                })}
              </div>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Select one or more difficulty levels.
              </p>
              <FieldError>{errors.difficulty_level}</FieldError>
            </div>
          </div>

          {mode === "create" && (
            <div className="grid grid-cols-3 gap-4">
              {/* Duration in Weeks */}
              <div>
                <Label>Duration (Weeks) *</Label>
                <TextInput
                  type="number"
                  min="1"
                  max="12"
                  value={formData.duration_weeks || ""}
                  onChange={(e) =>
                    handleInputChange(
                      "duration_weeks",
                      parseInt(e.target.value)
                    )
                  }
                  invalid={!!errors.duration_weeks}
                  placeholder="4"
                />
                <FieldError>{errors.duration_weeks}</FieldError>
              </div>

              {/* Training Days Per Week */}
              <div>
                <Label>Training Days/Week *</Label>
                <TextInput
                  type="number"
                  min="1"
                  max="7"
                  value={formData.training_days_per_week || ""}
                  onChange={(e) =>
                    handleInputChange(
                      "training_days_per_week",
                      parseInt(e.target.value)
                    )
                  }
                  invalid={!!errors.training_days_per_week}
                  placeholder="3"
                />
                <FieldError>{errors.training_days_per_week}</FieldError>
              </div>

              {/* Workout Duration */}
              <div>
                <Label>Workout Duration *</Label>
                <select
                  value={formData.workout_duration || ""}
                  onChange={(e) =>
                    handleInputChange("workout_duration", e.target.value)
                  }
                  className={selectClass(!!errors.workout_duration)}
                >
                  {getWorkoutDurationOptions().map(({ value, label }) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
                <FieldError>{errors.workout_duration}</FieldError>
              </div>
            </div>
          )}

          {/* Description */}
          <div>
            <Label>Description</Label>
            <textarea
              rows={4}
              value={formData.description || ""}
              onChange={(e) => handleInputChange("description", e.target.value)}
              className={fieldClass(!!errors.description)}
              placeholder="Enter program description (optional)"
            />
            <FieldError>{errors.description}</FieldError>
          </div>

          {/* Equipment - Only show in edit mode */}
          {mode === "edit" && (
            <div>
              <Label>Equipment</Label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={equipmentInput}
                  onChange={(e) => setEquipmentInput(e.target.value)}
                  onKeyDown={handleEquipmentKeyDown}
                  maxLength={100}
                  className={`flex-1 rounded-md border px-3 py-2 shadow-sm focus:border-biafit-primary focus:outline-none focus:ring-2 focus:ring-biafit-primary dark:border-gray-600 dark:bg-navy-700 dark:text-white ${
                    errors.equipment ? "border-red-500" : "border-gray-300"
                  }`}
                  placeholder="Add equipment (e.g., Dumbbells, Resistance Bands)"
                />
                <button
                  type="button"
                  onClick={handleAddEquipment}
                  className="flex items-center gap-1 rounded-md bg-biafit-primary px-4 py-2 text-white transition-colors hover:bg-biafit-secondary"
                >
                  <MdAdd className="h-5 w-5" />
                  Add
                </button>
              </div>
              <FieldError>{errors.equipment}</FieldError>

              {/* Equipment Tags */}
              {formData.equipment && formData.equipment.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {formData.equipment.map((item: string, index: number) => (
                    <span
                      key={`equipment-${index}`}
                      className="inline-flex items-center rounded-full bg-blue-100 px-3 py-1 text-sm font-medium text-blue-800 dark:bg-blue-900 dark:text-blue-300"
                    >
                      {item}
                      <button
                        type="button"
                        onClick={() => handleRemoveEquipment(index)}
                        className="ml-2 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-200"
                      >
                        <MdClose className="h-4 w-4" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Press Enter or click Add to add equipment items (max 100
                characters each)
              </p>
            </div>
          )}
          {/* Visible to Users */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="is_visible"
              checked={formData.is_visible}
              onChange={(e) =>
                handleInputChange("is_visible", e.target.checked)
              }
              className="h-4 w-4 cursor-pointer rounded border-gray-300 text-biafit-primary focus:ring-biafit-primary"
            />
            <label
              htmlFor="is_visible"
              className="ml-2 block cursor-pointer text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              Visible to users
            </label>
          </div>
        </div>
      </div>
    </ModalShell>
  );
};

export default ProgramModal;
